import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, getToken } from "../api";
import MemoTab from "../MemoTab";

const TYPE_LABEL = {
  CUSTOMER: "고객사", VENDOR: "매입처", PARTNER: "협력사", FREELANCER: "프리랜서", GOV: "기관", INTERNAL: "내부",
};

const DOC_TYPES = [
  ["BIZ_REG", "사업자등록증"],
  ["BANKBOOK", "통장사본"],
  ["ETC", "기타"],
];
const DOC_LABEL = Object.fromEntries(DOC_TYPES);

const EMPTY_CONTACT = { name: "", position: "", phone: "", email: "", is_primary: false };

export default function CompanyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("info");

  async function load() {
    setError("");
    try {
      setCompany(await api.get(`/companies/${id}`));
    } catch (err) {
      setError(err.message);
    }
  }
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function removeCompany() {
    if (!window.confirm("이 업체와 담당자·문서를 모두 삭제합니다. 계속할까요?")) return;
    try {
      await api.del(`/companies/${id}`);
      navigate("/sstoa/companies", { replace: true });
    } catch (err) {
      setError(err.message);
    }
  }

  if (error) return <div className="sstoa-error">{error}</div>;
  if (!company) return <div className="sstoa-empty">불러오는 중…</div>;

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <Link to="/sstoa/companies" className="sstoa-back">← 업체 목록</Link>
      </div>

      <div className="sstoa-topbar">
        <div>
          <h1 className="sstoa-title">
            {company.name} <span className="sstoa-tag" style={{ verticalAlign: "middle" }}>{TYPE_LABEL[company.type] || company.type}</span>
          </h1>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Link to={`/sstoa/companies/${id}/edit`} className="sstoa-btn">수정</Link>
          <button className="sstoa-btn sstoa-btn--danger" onClick={removeCompany}>삭제</button>
        </div>
      </div>

      <div className="sstoa-tabs" style={{ marginTop: 8 }}>
        <button type="button" className={`sstoa-tab ${tab === "info" ? "is-active" : ""}`} onClick={() => setTab("info")}>기본정보</button>
        <button type="button" className={`sstoa-tab ${tab === "contacts" ? "is-active" : ""}`} onClick={() => setTab("contacts")}>
          담당자<span className="sstoa-tab__cnt">{company.contacts.length}</span>
        </button>
        <button type="button" className={`sstoa-tab ${tab === "docs" ? "is-active" : ""}`} onClick={() => setTab("docs")}>
          보관 문서<span className="sstoa-tab__cnt">{company.documents.length}</span>
        </button>
        <button type="button" className={`sstoa-tab ${tab === "memo" ? "is-active" : ""}`} onClick={() => setTab("memo")}>메모</button>
      </div>
      <div className="sstoa-tabpane">
        {tab === "info" && (
          <div style={{ marginTop: 16 }}>
            <div className="sstoa-panel sstoa-panel--pad" style={{ maxWidth: 560 }}>
              <dl className="sstoa-meta">
                <dt>사업자등록번호</dt><dd>{company.biz_reg_no || "—"}</dd>
                <dt>대표자</dt><dd>{company.representative || "—"}</dd>
                <dt>업태</dt><dd>{company.biz_condition || "—"}</dd>
                <dt>업종</dt><dd>{company.biz_item || "—"}</dd>
                <dt>주소</dt><dd>{company.address || "—"}</dd>
              </dl>
            </div>
          </div>
        )}
        {tab === "contacts" && <Contacts companyId={id} contacts={company.contacts} onChange={load} />}
        {tab === "docs" && <Documents companyId={id} documents={company.documents} onChange={load} />}
        {tab === "memo" && <MemoTab path={`/companies/${id}`} field="note" value={company.note} onSaved={load} placeholder="업체 관련 메모를 자유롭게 입력하세요." />}
      </div>
    </div>
  );
}

/* ---------------- 담당자 ---------------- */
function Contacts({ companyId, contacts, onChange }) {
  const [open, setOpen] = useState(false);      // 폼 열림
  const [editId, setEditId] = useState(null);   // null = 신규 추가, 값 = 해당 담당자 수정
  const [form, setForm] = useState(EMPTY_CONTACT);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const upd = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: k === "is_primary" ? e.target.checked : e.target.value }));

  function startAdd() {
    setEditId(null); setForm(EMPTY_CONTACT); setError(""); setOpen(true);
  }
  function startEdit(c) {
    setEditId(c.id);
    setForm({
      name: c.name || "", position: c.position || "",
      phone: c.phone || "", email: c.email || "", is_primary: !!c.is_primary,
    });
    setError(""); setOpen(true);
  }
  function cancel() { setOpen(false); setEditId(null); setForm(EMPTY_CONTACT); }

  async function submit(e) {
    e.preventDefault();
    if (!form.name.trim()) { setError("이름은 필수입니다."); return; }
    setBusy(true); setError("");
    try {
      if (editId) {
        await api.post(`/contacts/${editId}`, form);   // 수정 (프록시가 PATCH 를 막아 POST 사용)
      } else {
        await api.post(`/companies/${companyId}/contacts`, form);  // 신규
      }
      cancel(); onChange();
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  }
  async function remove(cid) {
    if (!window.confirm("담당자를 삭제할까요?")) return;
    try { await api.del(`/contacts/${cid}`); onChange(); } catch (err) { setError(err.message); }
  }

  return (
    <div className="sstoa-section">
      <div className="sstoa-section__head">
        <h2>담당자</h2>
        {!open && <button className="sstoa-btn sstoa-btn--sm" onClick={startAdd}>+ 담당자 추가</button>}
      </div>

      {error && <div className="sstoa-error" style={{ marginBottom: 10 }}>{error}</div>}

      {open && (
        <form className="sstoa-panel sstoa-panel--pad" style={{ marginBottom: 12 }} onSubmit={submit}>
          <div style={{ fontWeight: 700, marginBottom: 10 }}>{editId ? "담당자 수정" : "담당자 추가"}</div>
          <div className="sstoa-row">
            <div className="sstoa-field"><label>이름 <span className="req">*</span></label>
              <input className="sstoa-input" value={form.name} onChange={upd("name")} disabled={busy} /></div>
            <div className="sstoa-field"><label>직급</label>
              <input className="sstoa-input" value={form.position} onChange={upd("position")} disabled={busy} /></div>
          </div>
          <div className="sstoa-row" style={{ marginTop: 12 }}>
            <div className="sstoa-field"><label>전화</label>
              <input className="sstoa-input" value={form.phone} onChange={upd("phone")} disabled={busy} /></div>
            <div className="sstoa-field"><label>이메일</label>
              <input className="sstoa-input" type="email" value={form.email} onChange={upd("email")} disabled={busy} /></div>
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 6, margin: "12px 0", fontSize: 13 }}>
            <input type="checkbox" checked={form.is_primary} onChange={upd("is_primary")} disabled={busy} /> 주 담당자
          </label>
          <div className="sstoa-actions">
            <button type="submit" className="sstoa-btn sstoa-btn--primary sstoa-btn--sm" disabled={busy}>{editId ? "저장" : "추가"}</button>
            <button type="button" className="sstoa-btn sstoa-btn--sm" onClick={cancel}>취소</button>
          </div>
        </form>
      )}

      <div className="sstoa-panel">
        {contacts.length === 0 ? (
          <div className="sstoa-empty">등록된 담당자가 없습니다.</div>
        ) : (
          <table className="sstoa-table">
            <thead><tr><th>이름</th><th>직급</th><th>전화</th><th>이메일</th><th></th></tr></thead>
            <tbody>
              {contacts.map((c) => (
                <tr key={c.id} style={{ cursor: "default" }}>
                  <td style={{ fontWeight: 600 }}>
                    {c.name} {c.is_primary && <span className="sstoa-tag" style={{ marginLeft: 4 }}>주</span>}
                  </td>
                  <td>{c.position || "—"}</td>
                  <td>{c.phone ? <a className="sstoa-link" href={`tel:${c.phone.replace(/-/g, "")}`}>{c.phone}</a> : "—"}</td>
                  <td>{c.email ? <Link className="sstoa-link" to={`/sstoa/email?to=${encodeURIComponent(c.email)}`}>{c.email}</Link> : "—"}</td>
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    <button className="sstoa-btn sstoa-btn--sm" style={{ marginRight: 6 }} onClick={() => startEdit(c)}>수정</button>
                    <button className="sstoa-btn sstoa-btn--danger sstoa-btn--sm" onClick={() => remove(c.id)}>삭제</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* ---------------- 보관 문서 ---------------- */
function Documents({ companyId, documents, onChange }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [docType, setDocType] = useState("ETC");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function upload(e) {
    e.preventDefault();
    if (!file) { setError("파일을 선택하세요."); return; }
    setBusy(true); setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", docType);
      if (title) fd.append("title", title);
      await api.upload(`/companies/${companyId}/documents`, fd);
      setFile(null); setTitle(""); setDocType("ETC");
      e.target.reset();
      onChange();
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  }

  async function download(doc) {
    // 인증 헤더가 필요하므로 blob 으로 받아 저장
    try {
      const res = await fetch(`/api/sstoa/documents/${doc.id}/file`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error("다운로드에 실패했습니다.");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_name || `document-${doc.id}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) { setError(err.message); }
  }

  async function remove(docId) {
    if (!window.confirm("문서를 삭제할까요?")) return;
    try { await api.del(`/documents/${docId}`); onChange(); } catch (err) { setError(err.message); }
  }

  return (
    <div className="sstoa-section">
      <div className="sstoa-section__head"><h2>보관 문서</h2></div>

      {error && <div className="sstoa-error" style={{ marginBottom: 10 }}>{error}</div>}

      <form className="sstoa-panel sstoa-panel--pad" style={{ marginBottom: 12, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }} onSubmit={upload}>
        <div className="sstoa-field" style={{ flex: 1, minWidth: 180 }}>
          <label>파일</label>
          <input className="sstoa-input" type="file" onChange={(e) => setFile(e.target.files[0] || null)} disabled={busy} />
        </div>
        <div className="sstoa-field" style={{ minWidth: 130 }}>
          <label>종류</label>
          <select className="sstoa-select" value={docType} onChange={(e) => setDocType(e.target.value)} disabled={busy}>
            {DOC_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div className="sstoa-field" style={{ flex: 1, minWidth: 130 }}>
          <label>제목 (선택)</label>
          <input className="sstoa-input" value={title} onChange={(e) => setTitle(e.target.value)} disabled={busy} />
        </div>
        <button type="submit" className="sstoa-btn sstoa-btn--primary" disabled={busy}>{busy ? "업로드 중…" : "업로드"}</button>
      </form>

      <div className="sstoa-panel">
        {documents.length === 0 ? (
          <div className="sstoa-empty">보관된 문서가 없습니다.</div>
        ) : (
          <table className="sstoa-table">
            <thead><tr><th>종류</th><th>제목 / 파일명</th><th>크기</th><th></th></tr></thead>
            <tbody>
              {documents.map((d) => (
                <tr key={d.id} style={{ cursor: "default" }}>
                  <td>
                    <span className={`sstoa-tag${d.doc_type && d.doc_type !== "ETC" ? "" : " sstoa-tag--gray"}`}>
                      {DOC_LABEL[d.doc_type] || "기타"}
                    </span>
                  </td>
                  <td>
                    <button className="sstoa-link" style={{ background: "none", border: 0, padding: 0, cursor: "pointer", font: "inherit" }} onClick={() => download(d)}>
                      {d.title || d.original_name || `문서 ${d.id}`}
                    </button>
                    {d.title && d.original_name && (
                      <span style={{ color: "var(--s-fg-mute)", fontSize: 12, marginLeft: 8 }}>{d.original_name}</span>
                    )}
                  </td>
                  <td>{d.size != null ? `${Math.round(d.size / 1024)} KB` : "—"}</td>
                  <td style={{ textAlign: "right" }}>
                    <button className="sstoa-btn sstoa-btn--danger sstoa-btn--sm" onClick={() => remove(d.id)}>삭제</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
